import React, { useState } from 'react';
import { useClickOutside } from '@/hooks/common/customHooks';
import { ChevronDownIcon } from '@/components/ui/IconComponents';
import Avatar from './Avatar';

interface DropdownItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  active?: boolean;
}

interface UserDropdownProps {
  userEmail: string;
  userRole: string;
  items: DropdownItem[];
  onLogout: () => Promise<void>;
  className?: string;
}

/**
 * UserDropdown - Reusable user dropdown component
 * 
 * A dropdown component that displays user information and navigation options.
 * Can be used for admin, user, or any role-based navigation.
 * 
 * @param userEmail - The user's email address
 * @param userRole - The user's role (Admin, User, etc.)
 * @param items - Array of dropdown menu items
 * @param onLogout - Function to handle logout
 * @param className - Additional CSS classes
 */
const UserDropdown: React.FC<UserDropdownProps> = ({
  userEmail,
  userRole,
  items,
  onLogout,
  className = ''
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setIsDropdownOpen(false));

  const handleItemClick = (onClick: () => void) => {
    setIsDropdownOpen(false);
    onClick();
  };

  const handleLogoutClick = async () => {
    setIsDropdownOpen(false);
    await onLogout();
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-200 rounded-lg px-3 py-2 transition-all duration-200 bg-gray-100"
      >
        <Avatar name={userEmail} />
        <div className="text-left hidden sm:block">
          <div className="text-sm font-medium text-gray-900">{userRole}</div>
          <div className="text-xs text-gray-500 truncate max-w-32">{userEmail}</div>
        </div>
        <ChevronDownIcon isOpen={isDropdownOpen} />
      </button>

      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg ring-1 ring-gray-200 z-50 overflow-hidden">
          <div className="py-1">
            {/* User Info Header */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
              <div className="font-medium text-gray-900">{userRole} Account</div>
              <div className="text-sm text-gray-500 truncate">{userEmail}</div>
            </div>

            {/* Navigation Items */}
            {items.map((item) => (
              <button
                key={`dropdown-item-${item.label}`}
                onClick={() => handleItemClick(item.onClick)}
                className={`bg-white w-full text-left px-4 py-3 text-sm ${
                  item.active 
                    ? 'text-blue-600 bg-blue-50' 
                    : 'text-gray-700 hover:bg-gray-50'
                } transition-colors duration-150 flex items-center space-x-2`}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}

            {/* Logout Button */}
            <div className="border-t border-gray-100">
              <button
                onClick={handleLogoutClick}
                className="bg-white w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
